import { useEffect, useRef, useState } from "react";
import { FaBell } from "react-icons/fa";
import axios from "axios";
import { useAuth } from "./AuthProvider";
import { TRACK_EVENT_SELECTION_KEY } from "./TrackEventSelector";
import {
  NOTIFICATION_UNREAD_COUNT_URL,
  NOTIFICATION_USER_URL,
  NOTIFICATION_MARK_READ_URL,
  NOTIFICATION_DOCUMENT_DOWNLOAD_URL,
} from "./utils/apiUrlList";
import "./NotificationBell.css";

import { api } from "./utils/apiClientWithCsrf";

const POLL_MS = 30_000;

const getStoredTrackId = () => {
  try {
    const raw = localStorage.getItem(TRACK_EVENT_SELECTION_KEY);
    return raw ? JSON.parse(raw)?.trackId ?? null : null;
  } catch {
    return null;
  }
};

const getStoredEventId = () => {
  try {
    const raw = localStorage.getItem(TRACK_EVENT_SELECTION_KEY);
    return raw ? JSON.parse(raw)?.eventId ?? null : null;
  }
  catch {
    return null;
  }
};

const formatTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const NotificationBell = () => {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [trackId, setTrackId] = useState(getStoredTrackId);
  const [eventId, setEventId] = useState(getStoredEventId);
  const panelRef = useRef(null);

  const openDoc = async (e, doc) => {
    e.stopPropagation();
    try {
      let url = doc.downloadUrl;
      if (!url) {
        const res = await api.get(NOTIFICATION_DOCUMENT_DOWNLOAD_URL(doc.id));
        url = res.data.url;
      }
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      // silently fail
    }
  };

  // Keep trackId and eventId in sync when the user changes track/event.
  // Also clear stale items immediately so the bell never shows old data.
  useEffect(() => {
    const onTrackChanged = () => {
      setTrackId(getStoredTrackId());
      setEventId(getStoredEventId());
      setItems([]);
    };
    window.addEventListener("track-changed", onTrackChanged);
    return () => window.removeEventListener("track-changed", onTrackChanged);
  }, []);


  // Poll unread count every 30 s (scoped to current track if one is selected).
  useEffect(() => {
    if (!userId) return;

    const fetchCount = () => {
      api
        .get(NOTIFICATION_UNREAD_COUNT_URL(userId, trackId , eventId))
        .then(({ data }) => setUnread(data.count ?? 0))
        .catch(() => {});
    };

    fetchCount();
    const timer = setInterval(fetchCount, POLL_MS);
    return () => clearInterval(timer);
  }, [userId, trackId , eventId]);

  // Load full list whenever the panel opens or the track changes.
  useEffect(() => {
    if (!open || !userId) return;
    api
      .get(NOTIFICATION_USER_URL(userId, trackId , eventId))
      .then(({ data }) => {
        if (Array.isArray(data)) setItems(data);
      })
      .catch(() => {});
  }, [open, userId, trackId, eventId]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const toggle = () => {
    setOpen((o) => {
      if (!o) setUnread(0);
      return !o;
    });
  };

  const markRead = (id) => {
    if (!userId) return;
    api
      .post(NOTIFICATION_MARK_READ_URL(id, userId))
      .then(() =>
        setItems((prev) =>
          prev.map((n) => (n.id === id ? { ...n, read: true } : n))
        )
      )
      .catch(() => {});
  };

  return (
    <div className="notif-bell" ref={panelRef}>
      <button
        type="button"
        className="notif-bell-button"
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Notifications${unread ? `, ${unread} unread` : ""}`}
        title="Notifications"
      >
        <FaBell size={18} />
        {unread > 0 && (
          <span className="notif-bell-badge">{unread > 99 ? "99+" : unread}</span>
        )}
      </button>

      {open && (
        <div className="notif-panel" role="menu">
          <div className="notif-panel-header">
            <span>Notifications</span>
          </div>
          <div className="notif-panel-list">
            {items.length === 0 ? (
              <div className="notif-empty">No notifications yet</div>
            ) : (
              items.map((n) => (
                <div
                  key={n.id}
                  className={`notif-item${n.read ? " notif-item--read" : ""}`}
                  onClick={() => !n.read && markRead(n.id)}
                  style={{ cursor: n.read ? "default" : "pointer" }}
                >
                  {n.priority && n.priority !== "MEDIUM" && (
                    <span className={`notif-priority notif-priority--${n.priority.toLowerCase()}`}>
                      {n.priority}
                    </span>
                  )}
                  <div className="notif-item-title">{n.title}</div>
                  <div className="notif-item-message">{n.message}</div>
                  {n.documents && n.documents.length > 0 && (
                    <div className="notif-attachments">
                      {n.documents.map((doc) => {
                        const isImage = doc.contentType && doc.contentType.startsWith("image/");
                        return (
                          <div key={doc.id} className="notif-file-chip">
                            <span className={`notif-file-badge ${isImage ? "notif-file-badge--img" : "notif-file-badge--pdf"}`}>
                              {isImage ? "IMG" : "PDF"}
                            </span>
                            <span className="notif-file-name" title={doc.fileName}>
                              {doc.fileName}
                            </span>
                            <button
                              type="button"
                              className="notif-dl-btn"
                              onClick={(e) => openDoc(e, doc)}
                              title={`Download ${doc.fileName}`}
                            >
                              <svg width="11" height="11" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6 1v7M3 5.5l3 3 3-3M1 10h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              Download
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="notif-item-meta">
                    {n.createdBy ? `${n.createdBy} · ` : ""}
                    {formatTime(n.createdAt)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
